const DEFAULT_RETURN_TO = "/dashboard";
const RETURN_TO_STORAGE_KEY = "clm-web.auth.returnTo";

export function getDefaultReturnTo(): string {
  return DEFAULT_RETURN_TO;
}

export function buildReturnToFromLocation(
  location: Pick<Location, "pathname" | "search" | "hash">,
): string {
  const pathname = normalizePathname(location.pathname);
  return `${pathname}${location.search ?? ""}${location.hash ?? ""}`;
}

export function normalizeReturnTo(
  value: string | undefined | null,
  fallback = DEFAULT_RETURN_TO,
): string {
  if (!value) {
    return fallback;
  }

  const candidate = value.trim();
  if (!candidate || candidate.startsWith("//")) {
    return fallback;
  }

  if (candidate.startsWith("/")) {
    return candidate;
  }

  if (candidate.startsWith("http://") || candidate.startsWith("https://")) {
    return normalizeAbsoluteReturnTo(candidate, fallback);
  }

  return fallback;
}

// Blocked storage throws on access; sign-in and the callback must work without it.
export function persistReturnTo(value: string): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(RETURN_TO_STORAGE_KEY, normalizeReturnTo(value));
  } catch {
    // Nothing persisted: a callback without a `returnTo` query lands on the default page.
  }
}

export function consumePersistedReturnTo(fallback = DEFAULT_RETURN_TO): string {
  if (typeof window === "undefined") {
    return fallback;
  }

  let value: string | null;
  try {
    value = window.sessionStorage.getItem(RETURN_TO_STORAGE_KEY);
  } catch {
    return fallback;
  }
  try {
    window.sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
  } catch {
    // The value was read; a leftover copy is replaced by the next sign-in.
  }
  return normalizeReturnTo(value, fallback);
}

function normalizeAbsoluteReturnTo(value: string, fallback: string): string {
  if (typeof window === "undefined") {
    return fallback;
  }

  try {
    const parsed = new URL(value);

    if (parsed.origin !== window.location.origin) {
      return fallback;
    }

    return `${normalizePathname(parsed.pathname)}${parsed.search}${parsed.hash}`;
  } catch {
    return fallback;
  }
}

function normalizePathname(pathname: string): string {
  if (!pathname || pathname.trim().length === 0) {
    return "/";
  }

  return pathname.startsWith("/") ? pathname : `/${pathname}`;
}
